import {
  IDDocumentType,
  ProvidedDocumentsType,
  VerificationType,
} from "@amzn/client-workbench-api-model";
import { VideoRecording } from "@amzn/itsvideo-recorder";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Row, View } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import {
  Spinner,
  SpinnerColorScheme,
} from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { withTooltip } from "@amzn/stencil-react-components/tooltip";
import { trackStructEvent } from "@snowplow/browser-tracker";
import { AxiosResponse } from "axios";
import React, {
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "react-query";
import { RIVExp } from "src/@types/common";

import {
  S3DocUploadRequest,
  submitRetryVideoRecordings,
  submitVideoRecordings,
  uploadDocToS3,
  VerificationStatus,
} from "@/apis/IdentityCheckHelper";
import { Logger } from "@/apis/Logger";
import { Metrics } from "@/apis/Metrics";
import { ChatWithITSupportFlyoutLink } from "@/components/GetSupport";
import { RefPageValue } from "@/components/GetSupport/ChatWithITSupport/types";
import {
  clearAiRetryContext,
  getAiRetryContext,
  setAiRetryInitiated,
  setDocsSubmitted,
} from "@/components/IdentityCheck/helper/rivHelper";
import { getRetryDelayMs, shouldRetry } from "@/helpers/react-query-helper";
import { toVerificationType } from "@/helpers/verification-status-helper";
import { clearStartedWaitingEpochMillis } from "@/hooks/useUserProgress";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

const SUBMIT_REVIEW_BTN_CLICK_METRIC = "SubmitReviewButtonClick";

interface SubmitVideoButtonProps {
  /*** The Snowplow event category ***/
  eventCategory: Day1EventCategory;

  /**
   * For full remote ID verification only (FRIV):
   * Callback used to navigate the client back to the main ID verification steps on clicking
   * the button to submit for review at the end of each record step
   */
  clearSelectedStep?: () => void;

  /**
   * The reference to the FullIdVerification container element used to calculate the window
   * coordinate to scroll back to after the client completes ID recording steps.
   */
  fullIdVerificationElementRef?: RefObject<HTMLDivElement>;

  /**
   * Who mounted this component? Used for metrics collection on sub components.
   */
  refPage?: RefPageValue;
}

/**
 * A button that uploads all video recordings to the S3 buckets
 * @constructor
 */
export default function SubmitVideoButton({
  eventCategory,
  clearSelectedStep,
  fullIdVerificationElementRef,
  refPage,
}: SubmitVideoButtonProps): JSX.Element {
  const { state, dispatch } = useContext(AppContext);
  const [videoSubmitted, setVideoSubmitted] = useState(false);
  const intl = useIntl();
  const Tooltip = withTooltip({ useTriggerWrapper: false })(View);
  const retryContext = useMemo(() => getAiRetryContext(), []);
  const shouldUpload = retryContext
    ? retryContext.documentTypes.every((docType) =>
        state.documents.some((doc) => doc.documentType === docType)
      )
    : state.documents.length === state.requiredDocsWithGesturesCount;
  const abortController = new AbortController();
  const [anyUploadFailed, setAnyUploadFailed] = useState(false);
  const [requestsToRetry, setRequestsToRetry] = useState<S3DocUploadRequest[]>(
    []
  );
  const rivExp: RIVExp =
    state.requiredDocsWithGesturesCount > 1 ? "FRIV" : "LRIV";
  const metricsPublisherHandle =
    Metrics.publisherInstance.newChildActionPublisherForMethod(rivExp);

  /**
   * Build the issuing country + form type the new hire has currently selected,
   * in the shape the create / retry APIs expect. Used both for the normal
   * submit and — on an AI-quality retry — to stamp the re-uploaded artifact's
   * S3 metadata with the document the new hire ultimately chose.
   */
  const buildProvidedDocuments = (): ProvidedDocumentsType =>
    state.requiredDocsWithGesturesCount >= 3
      ? {
          primary: {
            country: state.primaryIdSelection?.issuingCountry ?? "",
            formType: state.primaryIdSelection?.documentType ?? "",
          },
          secondary: {
            formType: state.secondaryIdProvided,
          },
        }
      : {
          primary: {
            country: state.primaryIdSelection?.issuingCountry ?? "",
            formType: state.primaryIdSelection?.documentType ?? "",
          },
        };

  /**
   * React-Query hook to upload the video recordings to the S3 buckets. Updates the verification status to PENDING
   * when the video recordings upload is successful to display a static wait time view.
   */
  const {
    mutate: submitRecordings,
    isSuccess,
    isError: isSubmitVideosError,
    isLoading: isSubmitVideosLoading,
    reset: resetSubmitVideoQuery,
  } = useMutation(
    ({
      verificationType,
      documents,
      documentTypes,
    }: {
      verificationType: VerificationType;
      documents: VideoRecording[];
      documentTypes?: IDDocumentType[];
    }): Promise<S3DocUploadRequest[]> => {
      if (documentTypes) {
        return submitRetryVideoRecordings(
          verificationType,
          documents,
          documentTypes,
          abortController.signal,
          buildProvidedDocuments()
        );
      }
      return submitVideoRecordings(
        verificationType,
        documents,
        abortController.signal,
        buildProvidedDocuments(),
        state.selectedLanguage
      );
    },
    {
      onSuccess: () => {
        void handleAllDocsUploaded();
      },
      onError: (remainingRequests: S3DocUploadRequest[]) => {
        // Set the failed requests in the local state to retry them later
        setRequestsToRetry(remainingRequests);
      },
      retry: shouldRetry,
      retryDelay: getRetryDelayMs,
    }
  );

  const {
    mutate: uploadDocument,
    isLoading: isS3UploadLoading,
    reset: resetUploadQuery,
  } = useMutation(
    ({
      preSignedUrl,
      videoRecording,
    }: {
      preSignedUrl: string;
      videoRecording: VideoRecording;
    }): Promise<AxiosResponse> => {
      return uploadDocToS3(
        preSignedUrl,
        videoRecording.videoBlob,
        abortController.signal
      );
    },
    {
      onSuccess: (data, callParams) => {
        // TODO: Replace the requestsToRetry logic with a self contained retry mechanism.
        if (requestsToRetry.length > 0) {
          /**
           * We need to remove the S3 upload request that was retried successfully from the requests retry list.
           */
          const idx = requestsToRetry.findIndex(
            (request) =>
              request.videoRecording.documentType ===
              callParams.videoRecording.documentType
          );

          if (idx >= 0) {
            // Delete the successful requests from the retry list
            requestsToRetry.splice(idx, 1);
          } else {
            void Logger.errorErrorLike(
              `Unable to find the failed request for preSigned url, the video reupload has failed.`,
              undefined
            );
          }

          const allRetriesSucceed = requestsToRetry.every((req) => req.success);
          // If all previous failed requests were completed successfully, we can move to the static wait time view
          if (allRetriesSucceed) {
            void handleAllDocsUploaded();
          }
        }
      },
      onError: () => {
        setAnyUploadFailed(true);
      },
      retry: shouldRetry,
      retryDelay: getRetryDelayMs,
    }
  );

  const isLoading = isS3UploadLoading || isSubmitVideosLoading;
  const isError = isSubmitVideosError || anyUploadFailed;

  // Make sure NHs have selected a Primary ID issuing country and type
  const isPrimaryIdTypeMissing =
    state.documents.findIndex(
      (doc) =>
        doc.documentType === "PRIMARY_ID_RECORDING" &&
        (!state.primaryIdSelection?.issuingCountry ||
          !state.primaryIdSelection?.documentType)
    ) >= 0;
  // Make sure NHs have selected whether their Secondary ID (if required) is government-issued or not
  const isSecondaryIdGovIssuedMissing =
    refPage === "ezo-identity-check-manual-full-sec" &&
    state.secondaryIdGovIssued === undefined;

  const isDisabled =
    isSuccess ||
    videoSubmitted ||
    isLoading ||
    isPrimaryIdTypeMissing ||
    isSecondaryIdGovIssuedMissing;

  /**
   * Update the current verification status to PENDING in the global app state to render the static wait time view.
   */
  const handleAllDocsUploaded = () => {
    clearStartedWaitingEpochMillis();
    setDocsSubmitted();
    if (getAiRetryContext()) {
      setAiRetryInitiated();
      clearAiRetryContext();
    }
    dispatch({
      type: ActionType.SET_DOCS_VERIFICATION_STATUS,
      docsVerificationStatus:
        rivExp === "LRIV"
          ? VerificationStatus.LIGHT_PENDING
          : VerificationStatus.FULL_PENDING,
    });
  };

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: refPage,
    });
  }, [refPage]);

  /**
   * Abort any ongoing requests when the component is unmounted.
   */
  useEffect(() => {
    return () => abortController.abort();
  }, []);

  /**
   * Only upload the documents when we have collected all required documents from NHs.
   */
  const handleClick = () => {
    metricsPublisherHandle.publishCounterMonitor(
      SUBMIT_REVIEW_BTN_CLICK_METRIC,
      1
    );

    // Capture the final selected issuing country code and primary ID type in the format of <countryCode>-<primaryIdType>
    trackStructEvent({
      category: eventCategory,
      action: Day1EventAction.Continue,
      label: `${state.primaryIdSelection?.issuingCountry ?? ""}-${
        state.primaryIdSelection?.documentType ?? ""
      }`,
    });

    // Capture the final selected secondary ID government-issued selection in both the action and the label
    trackStructEvent({
      category: Day1EventCategory.IDCheckFullSec,
      action: state.secondaryIdGovIssued
        ? Day1EventAction.SelectGovernmentIssuedId
        : Day1EventAction.SelectNonGovernmentIssuedId,
      label: state.secondaryIdGovIssued
        ? Day1EventAction.SelectGovernmentIssuedId
        : Day1EventAction.SelectNonGovernmentIssuedId,
    });

    if (shouldUpload) {
      // Retry the failed requests if there is any
      requestsToRetry.forEach((request) => {
        uploadDocument({
          preSignedUrl: request.preSignedUrl,
          videoRecording: request.videoRecording,
        });
      });

      if (requestsToRetry.length === 0) {
        if (retryContext) {
          const retryDocuments = state.documents.filter((doc) =>
            retryContext.documentTypes.includes(doc.documentType)
          );
          submitRecordings({
            verificationType: retryContext.verificationType,
            documents: retryDocuments,
            documentTypes: retryContext.documentTypes,
          });
        } else {
          submitRecordings({
            verificationType: toVerificationType(state.docsVerificationStatus),
            documents: state.documents,
          });
        }
      }
    } else {
      // Mark the last doc as submitted
      setVideoSubmitted(true);
      if (eventCategory === Day1EventCategory.IDCheckFullSec) {
        dispatch({
          type: ActionType.SET_SECONDARY_ID_PROVIDED,
          secondaryIdProvided: "secondary ID provided",
        });
      }
      dispatch({
        type: ActionType.SET_LAST_DOC_SUBMIT_CLICKED,
      });
      // FRIV: After recording an ID, go back to the main steps and reposition the window
      if (clearSelectedStep) {
        clearSelectedStep();

        // Subtractive adjustment to prevent scrolling past the record ID steps
        const oneFourthViewportHeight = window.innerHeight / 4;

        const topCoord = fullIdVerificationElementRef?.current
          ? fullIdVerificationElementRef.current.offsetTop -
            oneFourthViewportHeight
          : 0; // default to scrolling to the top of the window
        window.scrollTo({
          top: topCoord,
          left: 0,
          behavior: "smooth",
        });
      }
    }
  };

  /**
   * Button to submit video recording with an active state.
   */
  const SubmitButton = (): JSX.Element => {
    return (
      <Button
        variant={ButtonVariant.Primary}
        dataTestId="submitReviewBtn"
        onClick={handleClick}
        disabled={isDisabled}
      >
        {isLoading ? (
          <Spinner
            showText
            loadingText={intl.formatMessage({
              id: "identityCheck-button-loading",
            })}
            colorScheme={SpinnerColorScheme.Inverted}
            dataTestId="spinnerTestId"
          />
        ) : (
          <FormattedMessage id="identityCheck-button-submitReview" />
        )}
      </Button>
    );
  };

  /**
   * Button with a tooltip that informs NHs about that they have already submitted their video recordings.
   */
  const SubmitButtonDisabled = (): JSX.Element => {
    const i18nId = isPrimaryIdTypeMissing
      ? "identityCheck-button-tooltip-missingPrimaryIdType"
      : isSecondaryIdGovIssuedMissing
      ? "identityCheck-button-tooltip-missingSecondaryIdGovIssued"
      : "identityCheck-button-tooltip-disallowResubmit";
    return (
      <Tooltip
        tooltipText={intl.formatMessage(
          {
            id: i18nId,
          },
          { requiredDocuments: state.requiredDocsWithGesturesCount - 1 }
        )}
      >
        <SubmitButton />
      </Tooltip>
    );
  };

  return (
    <Row gridGap="S300">
      {isSuccess ||
      videoSubmitted ||
      isPrimaryIdTypeMissing ||
      isSecondaryIdGovIssuedMissing ? (
        <SubmitButtonDisabled />
      ) : (
        <SubmitButton />
      )}
      {isError && (
        <MessageBanner
          type={MessageBannerType.Error}
          isDismissible
          onDismissed={() => {
            resetSubmitVideoQuery();
            resetUploadQuery();
          }}
          dataTestId="messageBannerTestId"
        >
          <Text>
            <FormattedMessage
              id="identityCheck-errors-failedToSubmit"
              values={{
                link: (
                  <ChatWithITSupportFlyoutLink
                    localeMessageId="identityCheck-errors-chatWithIT"
                    metricProps={{
                      category: Day1EventCategory.BlockingError,
                      action: Day1EventAction.ChatWithSupport,
                      label: Day1EventLabel.GenericError,
                    }}
                  />
                ),
              }}
            />
          </Text>
        </MessageBanner>
      )}
    </Row>
  );
}
